/**
 * Persistence reconciliation rules (docs/persistence-audit-2026-07-24.md).
 * Pure, storage-free functions — unit-tested in ./persistence.test.ts; the
 * React wiring (effects, localStorage) lives in ./store.ts.
 */
import type { Surgery } from './types';

/** localStorage key recording which account the per-user cache belongs to. */
export const KEY_ACCOUNT = 'suturia:account:v1';

/**
 * Demo ("sample data") records are client-only and ephemeral: seed.ts marks
 * them `demo: true`, and records from older builds are recognized by their
 * stable `semilla-*` ids. They must never reach the server and must never
 * survive a login.
 */
export function isDemoSurgery(c: Surgery): boolean {
  return c.demo === true || c.id.startsWith('semilla-');
}

/** Drops demo records; returns the input reference when there are none. */
export function stripDemoSurgeries(list: Surgery[]): Surgery[] {
  return list.some(isDemoSurgery) ? list.filter((c) => !isDemoSurgery(c)) : list;
}

export type LoginReconcile = 'same-account' | 'anonymous-merge' | 'account-switch';

/**
 * How cached per-user data reconciles with a login (audit doc §4):
 * - same-account:    the cache mirrors THIS account — the server wins as-is
 *                    (deletion-safe: rows deleted on another device stay gone).
 * - anonymous-merge: no account on record — local rows are guest work (or a
 *                    cache from before account tracking) and merge-upload
 *                    into the account instead of being wiped.
 * - account-switch:  the cache belongs to a DIFFERENT account — purge it
 *                    before the new account's data loads (cross-account leak).
 */
export function decideLoginReconcile(
  storedAccount: string | null,
  currentAccount: string,
): LoginReconcile {
  if (storedAccount === currentAccount) return 'same-account';
  if (storedAccount === null) return 'anonymous-merge';
  return 'account-switch';
}

/**
 * anonymous-merge: server rows win for shared ids; local-only non-demo rows
 * are guest work to upload. Returns the merged view plus the upload set.
 */
export function mergeAnonymousIntoServer(
  local: Surgery[],
  server: Surgery[],
): { merged: Surgery[]; toUpload: Surgery[] } {
  const serverIds = new Set(server.map((c) => c.id));
  const toUpload = local.filter((c) => !isDemoSurgery(c) && !serverIds.has(c.id));
  return { merged: [...server, ...toUpload], toUpload };
}
